/**
 * Onglet Documents - Profil Unifie (PL + AC)
 * 
 * FRONT-08: Interface unifiee pour Prestataire Libre ET Accord Cadre
 * FRONT-21: Refonte affichage documents par chapitres thématiques
 * Utilise ProfileContext pour les donnees.
 * 
 * NOTE: Le logo est gere separement via /api/profile/logo (stocke dans storage, pas en DB)
 * 
 * @see CORE-11-upload-logo.md pour l'API logo
 * @see CORE-12 pour les documents non modifiables
 * @see FRONT-21-refonte-chapitres-documents.md pour la structure en chapitres
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import Button from '../../../composants/Button/Button';
import UploadDocumentModal from '../../../composants/UploadDocumentModal/UploadDocumentModal';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import { DOCUMENT_TYPES } from '../../../services/documentService';
import type { DocumentType } from '../../../services/documentService';
import { useDocumentPermissions } from '../../../hooks/useDocumentPermissions';
import '../../../composants/MesDocuments/MesDocuments.css';
import './TabDocuments.css';

// FRONT-21: Types pour les chapitres de documents
type DocumentChapterType = 'obligatoires' | 'ac_2025_2026' | 'ac_2027_2028';

interface DocumentSubSection {
  type: DocumentType | 'logo';
  label: string;
  note?: string; // Message informatif spécifique
  required?: boolean;
}

interface DocumentChapter {
  id: DocumentChapterType;
  title: string;
  description?: string; // Message en haut du chapitre
  visibleFor: 'all' | 'accord_cadre'; // Qui peut voir ce chapitre
  subSections: DocumentSubSection[];
  readOnly?: boolean; // Si true, tous les docs sont en lecture seule
}

// FRONT-21: Configuration des chapitres thématiques
const DOCUMENT_CHAPTERS: DocumentChapter[] = [
  {
    id: 'obligatoires',
    title: 'Documents obligatoires (à maintenir à jour)',
    visibleFor: 'all',
    subSections: [
      { type: 'logo', label: 'Logo entreprise', required: true },
      { type: DOCUMENT_TYPES.KBIS, label: 'Extrait Kbis', required: true },
      { type: DOCUMENT_TYPES.ATTESTATION_FISCALE, label: 'Attestation fiscale', required: true },
      { type: DOCUMENT_TYPES.ATTESTATION_SOCIALE, label: 'Attestation sociale', required: true },
      { type: DOCUMENT_TYPES.RC_PRO, label: 'Assurance responsabilité civile', required: true },
      {
        type: DOCUMENT_TYPES.GARANTIE_DECENNALE,
        label: 'Assurance garantie décennale',
        note: 'Pour les entreprises non soumises, chargez votre RCP.',
        required: true
      },
      { type: DOCUMENT_TYPES.QUALIFICATIONS, label: 'Certifications / qualifications', required: false },
      { type: 'cv_equipe' as DocumentType, label: 'CV de l\'équipe encadrante', required: false },
      { type: DOCUMENT_TYPES.RIB, label: 'RIB', required: true },
    ],
  },
  {
    id: 'ac_2025_2026',
    title: 'Accord-cadre édition 2025-2026',
    description: 'Ces documents ne sont pas modifiables.',
    visibleFor: 'all',
    readOnly: true,
    subSections: [
      { type: 'contrat_pdf' as DocumentType, label: 'Contrat (CCAP + AE)' },
      { type: 'cctp_travaux' as DocumentType, label: 'CCTP Travaux' },
      { type: 'cctp_ingenierie' as DocumentType, label: 'CCTP Ingénierie' },
    ],
  },
  // {
  //   id: 'ac_2027_2028',
  //   title: 'Accord-cadre édition 2027-2028',
  //   description: 'Documents à venir pour l\'\u00e9dition 2027-2028',
  //   visibleFor: 'all',
  //   readOnly: true,
  //   subSections: [], // Vide pour l'instant (futur)
  // },
];

// FRONT-08: Interface pour les contrats Centralis (AC uniquement)
interface ContratCentralis {
  id: number;
  numero: string;
  type: string;
  date_signature?: string;
  documents: Array<{
    id: number;
    nom: string;
    type: string;
    binaire?: string;
  }>;
}

// FRONT-21: Le composant détecte automatiquement le rôle pour afficher les chapitres appropriés
const TabDocuments: React.FC = () => {
  const { documents, uploadDocument, isSaving, profile, loadProfile } = useProfile();
  const { checkPermission } = useDocumentPermissions();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // FRONT-08: Etat pour les contrats Centralis (AC)
  const [contrats, setContrats] = useState<ContratCentralis[]>([]);
  const [isLoadingContrats, setIsLoadingContrats] = useState(false);

  // FRONT-21: Etat pour les documents communs (CCTP)
  const [documentsCommuns, setDocumentsCommuns] = useState<any[]>([]);
  const [isLoadingDocumentsCommuns, setIsLoadingDocumentsCommuns] = useState(false);

  // FRONT-21: Déterminer si l'utilisateur est titulaire d'un AC (a des contrats)
  const hasContinueContracts = useMemo(() => {
    const hasContracts = contrats.length > 0;
    console.log('📝 hasContinueContracts:', hasContracts, '- Nombre de contrats:', contrats.length);
    return hasContracts;
  }, [contrats]);

  // FRONT-21: Filtrer les chapitres visibles selon le profil
  const visibleChapters = useMemo(() => {
    const filtered = DOCUMENT_CHAPTERS.filter(chapter => {
      if (chapter.visibleFor === 'all' && chapter.id === 'obligatoires') return true;
      // Chapitres AC visibles uniquement si l'utilisateur a des contrats
      if (chapter.id === 'ac_2025_2026' || chapter.id === 'ac_2027_2028') {
        return hasContinueContracts;
      }
      return false;
    });
    console.log('📂 Chapitres visibles:', filtered.map(c => c.id));
    return filtered;
  }, [hasContinueContracts]);

  // FRONT-21: Charger les contrats pour détecter les titulaires AC
  useEffect(() => {
    loadContratsCentralis();
    loadDocumentsCommuns();
  }, []);

  const loadContratsCentralis = async () => {
    setIsLoadingContrats(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      // FRONT-08: Utiliser la route existante pour les contrats AC
      const response = await fetch(`${apiUrl}/documents/my-entite-contrats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📜 Contrats Centralis - Réponse API:', data);
        const loadedContrats = data.data?.contrats || [];
        console.log('📜 Contrats Centralis - Nombre chargés:', loadedContrats.length);
        setContrats(loadedContrats);
      } else {
        console.error('❌ Erreur API contrats:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur chargement contrats Centralis:', error);
    } finally {
      setIsLoadingContrats(false);
    }
  };

  const loadDocumentsCommuns = async () => {
    setIsLoadingDocumentsCommuns(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/documents-communs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📄 Documents communs - Réponse API:', data);
        // Gérer différentes structures de réponse
        // Laravel paginate retourne: { status: 'success', data: { data: [...], current_page, ... } }
        let docs = [];
        if (Array.isArray(data)) {
          docs = data;
        } else if (data.data) {
          // Si data.data est un objet de pagination, extraire data.data.data
          if (data.data.data && Array.isArray(data.data.data)) {
            docs = data.data.data;
          } else if (Array.isArray(data.data)) {
            docs = data.data;
          }
        }
        console.log('📄 Documents communs - Après parsing:', docs);
        setDocumentsCommuns(docs);
      } else {
        console.error('❌ Erreur API documents-communs:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur chargement documents communs:', error);
      setDocumentsCommuns([]);
    } finally {
      setIsLoadingDocumentsCommuns(false);
    }
  };

  // Grouper les documents par type et trier par date (plus recent en premier)
  const documentsByType = useMemo(() => {
    const grouped: Record<string, typeof documents> = {};
    documents.forEach(doc => {
      if (!grouped[doc.type]) {
        grouped[doc.type] = [];
      }
      grouped[doc.type].push(doc);
    });

    // FRONT-06: Trier chaque groupe par date decroissante (plus recent en premier)
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at).getTime();
        const dateB = new Date(b.updated_at || b.created_at).getTime();
        return dateB - dateA; // Decroissant
      });
    });

    return grouped;
  }, [documents]);

  // Ouvrir modal d'upload
  const handleFileUpload = (type: DocumentType, label: string) => {
    setSelectedDocType(type);
    setSelectedLabel(label);
    setUploadModalOpen(true);
  };

  // Upload du document
  const handleUploadDocument = async (file: File, type: string, commentaire?: string) => {
    if (!selectedDocType) return;
    await uploadDocument(file, selectedDocType, commentaire);
    setUploadModalOpen(false);
    setSelectedDocType(null);
    setSelectedLabel('');
  };

  // Upload du logo (route spécifique /api/profile/logo)
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation côté client
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Format non accepté. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2 Mo
    if (file.size > maxSize) {
      setLogoError('Le fichier est trop volumineux (max 2 Mo).');
      return;
    }

    setLogoError(null);
    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/profile/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'upload du logo');
      }

      // Recharger le profil pour avoir le nouveau logo
      await loadProfile();
    } catch (error: any) {
      console.error('Erreur upload logo:', error);
      setLogoError(error.message || 'Erreur lors de l\'upload du logo');
    } finally {
      setIsUploadingLogo(false);
      // Reset input pour permettre de re-sélectionner le même fichier
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Supprimer le logo (route DELETE /api/profile/logo)
  const handleDeleteLogo = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
      return;
    }

    setLogoError(null);
    setIsDeletingLogo(true);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/profile/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression du logo');
      }

      // Recharger le profil
      await loadProfile();
    } catch (error: any) {
      console.error('Erreur suppression logo:', error);
      setLogoError(error.message || 'Erreur lors de la suppression du logo');
    } finally {
      setIsDeletingLogo(false);
    }
  };

  // FRONT-21: Rendu d'un chapitre
  const renderChapter = (chapter: DocumentChapter) => {
    // Si chapitre futur (pas de subSections), afficher message
    if (chapter.subSections.length === 0) {
      return (
        <div key={chapter.id} className="document-chapter">
          <h1 className="chapter-header">{chapter.title}</h1>
          {chapter.description && (
            <div className="chapter-description">{chapter.description}</div>
          )}
          <div className="chapter-content">
            <p className="info-text" style={{ fontStyle: 'italic' }}>
              Les documents pour cette édition seront disponibles lors de votre
              souscription à l'accord-cadre 2027-2028.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div key={chapter.id} className="document-chapter">
        <h1 className="chapter-header">{chapter.title}</h1>
        {chapter.description && (
          <div className="chapter-description">{chapter.description}</div>
        )}
        <div className="chapter-content">
          {chapter.subSections.map(subSection =>
            renderSubSection(subSection, chapter.readOnly)
          )}
        </div>
      </div>
    );
  };

  // FRONT-21: Rendu d'un sous-chapitre (section document)
  const renderSubSection = (
    subSection: DocumentSubSection,
    chapterReadOnly: boolean = false
  ) => {
    // Cas spécial : Logo
    if (subSection.type === 'logo') {
      return renderLogoSection(subSection);
    }

    // Cas spécial : Documents AC (contrat_pdf, cctp_travaux, cctp_ingenierie)
    if (['contrat_pdf', 'cctp_travaux', 'cctp_ingenierie'].includes(subSection.type as string)) {
      return renderContratSection(subSection);
    }

    // Documents standards
    const docs = documentsByType[subSection.type] || [];
    const hasDocuments = docs.length > 0;
    const permission = checkPermission(subSection.type, hasDocuments);
    const canUpload = !chapterReadOnly && permission.canUpload;

    // Formater les documents pour DocumentsList
    const formattedDocs = docs.map(doc => ({
      id: doc.id,
      type: doc.type_label || doc.type,
      commentaire: doc.commentaire || undefined,
      updated_at: doc.updated_at || doc.created_at,
      key: doc.key,
      category: 'entreprise' as const,
      filename: doc.filename || `${doc.type_label || subSection.label}.pdf`
    }));

    return (
      <section key={subSection.type} className="document-section">
        <h2 className="subsection-header">
          {subSection.label}
          {subSection.required && <span className="required-asterisk">*</span>}
          {hasDocuments && ` (${docs.length})`}
        </h2>

        <div className="section-content">
          {hasDocuments ? (
            <>
              <DocumentsList documents={formattedDocs} variant="inline" showIcon={false} size="smaller" />
              <div className="add-document-container">
                {canUpload ? (
                  <Button
                    onClick={() => handleFileUpload(subSection.type, subSection.label)}
                    value="Actualiser le document"
                    variant="secondary"
                    size="small"
                    disabled={isSaving}
                  />
                ) : (
                  <span className="info-message" style={{ fontStyle: 'italic' }}>
                    &nbsp;{permission.message}
                  </span>
                )}
              </div>
              {/* Message informatif spécifique */}
              {subSection.note && (
                <div className="info-message" style={{ fontStyle: 'italic', textAlign: 'left', paddingTop: '0.5rem' }}>
                  &nbsp;{subSection.note}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="add-document-container" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                {!chapterReadOnly ? (
                  <>
                    <span className="info-text" style={{ padding: 0, margin: 0, fontStyle: 'italic' }}>&nbsp;Aucun document</span>
                    <Button
                      onClick={() => handleFileUpload(subSection.type, subSection.label)}
                      value="Ajouter un document"
                      variant="primary"
                      size="small"
                      disabled={isSaving}
                    />
                  </>
                ) : (
                  <span className="info-text" style={{ padding: 0, margin: 0, fontStyle: 'italic' }}>&nbsp;Aucun document</span>
                )}
              </div>
              {/* Message informatif spécifique */}
              {subSection.note && (
                <div className="info-message" style={{ fontStyle: 'italic', textAlign: 'left', paddingTop: '0.5rem' }}>
                  &nbsp;{subSection.note}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    );
  };

  // FRONT-21: Rendu de la section Contrat Centralis (AC)
  const renderContratSection = (subSection: DocumentSubSection) => {
    const documentTypeKey = subSection.type as string;

    // Si c'est un CCTP, utiliser les documents communs
    if (documentTypeKey === 'cctp_travaux' || documentTypeKey === 'cctp_ingenierie') {
      const cctpType = documentTypeKey === 'cctp_travaux' ? 'CCTP-Travaux' : 'CCTP-Ingénierie';
      const searchTerm = documentTypeKey === 'cctp_travaux' ? 'travaux' : 'ingenierie';
      console.log('🔍 CCTP Filter - Type recherché:', cctpType, '- Terme de recherche:', searchTerm);
      console.log('🔍 CCTP Filter - Documents communs disponibles:', documentsCommuns);
      const cctpDocs = Array.isArray(documentsCommuns)
        ? documentsCommuns.filter(doc => {
          // Normaliser le type en enlevant espaces, tirets, accents et convertir en minuscules
          const normalizedDocType = doc.type?.toLowerCase()
            .replace(/[-\s]/g, '')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
          const normalizedSearch = searchTerm.toLowerCase()
            .replace(/[-\s]/g, '')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const isCCTP = normalizedDocType.includes('cctp');
          const matchesTerm = normalizedDocType.includes(normalizedSearch);
          const match = isCCTP && matchesTerm;
          console.log(`  - Doc ${doc.id} (${doc.type}): normalized="${normalizedDocType}" ${match ? '✅ Match' : '❌ No match'}`);
          return match;
        })
        : [];
      console.log('🔍 CCTP Filter - Documents filtrés:', cctpDocs);

      // Formater les documents pour DocumentsList
      const formattedDocs = cctpDocs.map(doc => ({
        id: doc.id,
        type: doc.type || cctpType,
        commentaire: doc.commentaire,
        updated_at: doc.updated_at,
        category: 'commun' as const,
        filename: doc.nom || `${cctpType}.pdf`
      }));

      return (
        <section key={subSection.type} className="document-section">
          <h2 className="subsection-header">{subSection.label}</h2>
          <div className="section-content">
            {isLoadingDocumentsCommuns ? (
              <div className="loading-text">Chargement des documents...</div>
            ) : formattedDocs.length > 0 ? (
              <DocumentsList documents={formattedDocs} variant="inline" size="smaller" showIcon={false} />
            ) : (
              <></>
            )}
          </div>
        </section>
      );
    }

    // Pour les contrats, convertir en format DocumentsList
    const documentType = 'Contrat';

    // Formater les contrats comme des documents pour DocumentsList
    const formattedContrats = contrats
      .map(contrat => {
        const contractDoc = contrat.documents?.find(d => d.type === documentType);
        if (!contractDoc?.binaire) return null;

        return {
          id: contrat.id,
          type: documentType,
          commentaire: '',
          updated_at: contrat.date_signature || new Date().toISOString(),
          category: 'accord_cadre' as const,
          filename: contractDoc.nom || contrat.numero || `Contrat_${contrat.id}.pdf`,
          // Stocker le binaire pour le téléchargement
          binaire: contractDoc.binaire
        };
      })
      .filter(Boolean) as any[];

    return (
      <section key={subSection.type} className="document-section">
        <h2 className="subsection-header">{subSection.label}</h2>
        <div className="section-content">
          {isLoadingContrats ? (
            <div className="loading-text">Chargement des contrats...</div>
          ) : formattedContrats.length > 0 ? (
            <DocumentsList documents={formattedContrats} variant="inline" size="smaller" showIcon={false} />
          ) : (
            <></>
          )}
        </div>
      </section>
    );
  };

  // Rendu de la section Logo (stocké dans storage, pas en DB)
  const renderLogoSection = (subSection: DocumentSubSection) => {
    const entite = profile?.entite;
    const hasLogo = !!entite?.logo_path;
    // Utiliser logo_url directement depuis l'API (URL complète générée par le backend)
    const logoUrl = entite?.logo_url || null;
    const isLoading = isUploadingLogo || isDeletingLogo;

    return (
      <section key="logo" className="document-section">
        <h2 className="subsection-header">
          {subSection.label}
          {subSection.required && <span className="required-asterisk">*</span>}
        </h2>
        <div className="section-content">
          <div className="logo-section-container">
            {/* Aperçu du logo */}
            <div className="logo-preview-box">
              {hasLogo && logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo entreprise"
                  className="logo-preview-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML = '<span class="logo-preview-text">Aperçu non disponible</span>';
                    }
                  }}
                />
              ) : (
                <span className="logo-preview-text">Aucun logo</span>
              )}
            </div>

            {/* Informations et boutons */}
            <div className="logo-info-container">
              <p className="logo-info-text">
                <strong>Formats acceptés :</strong> JPG, PNG, GIF, WebP
              </p>
              <p className="logo-info-text">
                <strong>Taille max :</strong> 2 Mo
              </p>

              {logoError && (
                <p className="logo-error-text">{logoError}</p>
              )}

              {/* Input file caché */}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />

              <div className="logo-buttons-container">
                <Button
                  onClick={() => logoInputRef.current?.click()}
                  value={isUploadingLogo ? "Upload en cours..." : (hasLogo ? "Modifier le logo" : "Ajouter un logo")}
                  variant="primary"
                  size="small"
                  disabled={isLoading || isSaving}
                />

                {hasLogo && (
                  <Button
                    onClick={handleDeleteLogo}
                    value={isDeletingLogo ? "Suppression..." : "Supprimer"}
                    variant="secondary"
                    size="small"
                    disabled={isLoading || isSaving}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="mes-documents">
      {/* FRONT-21: Affichage des chapitres selon le profil (PL ou AC) */}
      {visibleChapters.map(chapter => renderChapter(chapter))}

      {/* Modal d'upload */}
      {uploadModalOpen && selectedDocType && (
        <UploadDocumentModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedDocType(null);
            setSelectedLabel('');
          }}
          onUpload={handleUploadDocument}
          preselectedType={selectedLabel}
        />
      )}
    </div>
  );
};

export default TabDocuments;
